import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Avatar, Box, Grid, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HandymanIcon from '@mui/icons-material/Handyman';
import WorkIcon from '@mui/icons-material/Work';
import ExtensionIcon from '@mui/icons-material/Extension';
import {
  CreatorRole,
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessListForChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConstructionIcon from '@mui/icons-material/Construction';
import theme from '../../theme';
import { useChangeStore } from '../../store/changeStore';
import {
  formatNumber,
  getSumOfMaterialAndService,
} from '../../utils/getTotalTime';
import { useUserStore } from '../../store/userStore';
import {
  getDepartmentById,
  stringAvatar,
  stringToColor,
} from '../../utils/utils';

export type ProcessItemInfoProps = {
  workPackages?: ListWorkPackageDto[];
  workMaterials?: ListWorkMaterialDto[];
  workServices?: ListWorkServiceDto[];
  calculatedHours?: number | null;
  releasedHours?: number | null;
  timeImpact: boolean | null;
  process: ProcessListForChangeDto;
  showCalculation: boolean;
  showImplementation: boolean;
  userHasFullAccess?: boolean;
};

const ProcessItemInfo = observer(
  ({
    releasedHours,
    calculatedHours,
    workPackages,
    timeImpact,
    process,
    workServices,
    workMaterials,
    showImplementation,
    showCalculation,
    userHasFullAccess,
  }: ProcessItemInfoProps) => {
    const { change } = useChangeStore();

    const { t } = useTranslation();

    const { users } = useUserStore();

    const department = getDepartmentById(
      process.pId,
      change?.commonProject?.navProcessToCommonProjectRelations,
    );
    const [userName, setuserName] = useState('');

    const sumHours =
      (workPackages &&
        workPackages.reduce((acc, curr) => {
          if (curr.hours) {
            return acc + curr.hours;
          }
          return acc + 0;
        }, 0)) ||
      0;

    const notAffected = process.affected;

    const hasFiles = workPackages && workPackages.some(wP => wP.hasAttachments);

    const implementedCount =
      workPackages?.filter(wp => wp.status === 'IMPLEMENTED').length || 0;

    const sumServiceMaterialKTM = getSumOfMaterialAndService(
      workServices,
      workMaterials,
      CreatorRole.CoreTeamMember,
    );

    const sumServiceMaterialCalc = getSumOfMaterialAndService(
      workServices,
      workMaterials,
      CreatorRole.CalculationResponsible,
    );

    const sumServiceMaterialCM = getSumOfMaterialAndService(
      workServices,
      workMaterials,
      CreatorRole.ChangeManager,
    );

    useEffect(() => {
      (async () => {
        try {
          let user = '';
          if (department?.temporaryCoreTeamMember) {
            user = department?.temporaryCoreTeamMember.user.displayName;
            setuserName(user);
          } else {
            user = department?.owner ?? '';

            if (user) {
              const currentUser = users.find(u => u.id === user);
              if (currentUser) setuserName(currentUser?.displayName);
            }
          }
        } catch (e) {
          console.error(e);
        }
      })();
    }, [
      department?.owner,
      department?.temporaryCoreTeamMember,
      process,
      users,
    ]);

    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '22px',
          justifyContent: 'space-between',
          '@media (max-width: 440px)': {
            flexDirection: 'column',
            gap: '8px',
          },
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box sx={{ minWidth: 140, gap: 1, display: 'flex' }}>
            {timeImpact && (
              <AccessTimeIcon sx={{ color: theme.palette.primary.light }} />
            )}
            {hasFiles && (
              <AttachFileIcon sx={{ color: theme.palette.primary.light }} />
            )}
            {process.plannedMaterial && (
              <ConstructionIcon sx={{ color: theme.palette.primary.light }} />
            )}
            {notAffected && <DoNotDisturbIcon sx={{ color: 'red' }} />}
            {process.evaluationStatus === 'APPROVED' && (
              <CheckCircleIcon sx={{ color: 'green' }} />
            )}
          </Box>

          <Box
            className="workContainer"
            sx={{ mr: 2, display: 'flex', flexDirection: 'row' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                marginRight: 1,
              }}
            >
              <Typography
                css={css`
                  font-size: 14px;
                  font-weight: 300;
                  color: #333333;
                `}
              >
                {(workPackages && workPackages.length) || 0}
              </Typography>
              <Tooltip title={t('workPackages.workPackages')} placement="right">
                <WorkIcon width={10} sx={{ color: 'red' }} />
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                marginRight: 1,
              }}
            >
              <Typography
                css={css`
                  font-size: 14px;
                  font-weight: 300;
                  color: #333333;
                `}
              >
                {(workMaterials &&
                  workMaterials.filter(
                    item => item.creatorRole === CreatorRole.CoreTeamMember,
                  ).length) ||
                  0}
              </Typography>
              <Tooltip title={t('material.materials')} placement="right">
                <ExtensionIcon width={10} sx={{ color: 'red' }} />
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                marginRight: 1,
              }}
            >
              <Typography
                css={css`
                  font-size: 14px;
                  font-weight: 300;
                  color: #333333;
                `}
              >
                {(workServices &&
                  workServices.filter(
                    item => item.creatorRole === CreatorRole.CoreTeamMember,
                  ).length) ||
                  0}
              </Typography>
              <Tooltip title={t('service.services')} placement="right">
                <HandymanIcon width={10} sx={{ color: 'red' }} />
              </Tooltip>
            </Box>
          </Box>

          {!showCalculation && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('workPackage.estimatedHoursKTM')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`${
                  department?.hourlyRate && sumHours
                    ? formatNumber(sumHours * department?.hourlyRate, 0)
                    : ' - '
                }€ / ${sumHours ? formatNumber(sumHours ?? 0, 0) : ' - '} h`}
              </Typography>
            </Box>
          )}
          {!showCalculation && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('calc.calculatedHours')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`${
                  (department?.hourlyRate &&
                    userHasFullAccess &&
                    calculatedHours &&
                    formatNumber(
                      calculatedHours * department?.hourlyRate,
                      0,
                    )) ||
                  ' - '
                }€ / ${
                  calculatedHours
                    ? formatNumber(calculatedHours ?? 0, 0)
                    : ' - '
                }h`}
              </Typography>
            </Box>
          )}
          {!showCalculation && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('workPackage.releasedHoursCTM')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`${
                  (department?.hourlyRate &&
                    userHasFullAccess &&
                    releasedHours &&
                    formatNumber(releasedHours * department?.hourlyRate, 0)) ||
                  ' - '
                }€ / ${
                  releasedHours ? formatNumber(releasedHours ?? 0, 0) : ' - '
                }h`}
              </Typography>
            </Box>
          )}
          {/* The updated code block for Mat&Service Cost */}
          {showCalculation && userHasFullAccess && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('material.materialAndServiceKTM')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`€ ${formatNumber(sumServiceMaterialKTM, 0)}`}
              </Typography>
            </Box>
          )}
          {showCalculation && userHasFullAccess && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('material.materialAndServiceCalc')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`€ ${formatNumber(sumServiceMaterialCalc, 0)}`}
              </Typography>
            </Box>
          )}

          {showCalculation && userHasFullAccess && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('material.materialAndServiceCM')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {`€ ${formatNumber(sumServiceMaterialCM, 0)}`}
              </Typography>
            </Box>
          )}
          {showImplementation &&
            userHasFullAccess &&
            releasedHours &&
            (change?.status === UpdateChangeDtoStatusEnum.Check ||
              change?.status ===
                UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
              change?.status ===
                UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
              change?.status ===
                UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved) && (
              <Typography
                variant="h5"
                color={theme.palette.primary.dark}
                css={css`
                  padding: 8px;
                  background-color: #f8f8f8;
                  width: max-content;
                `}
              >{`${implementedCount}/${workPackages?.length} ${t(
                'workPackage.wp',
              )} ${t('implementation.implemented')}`}</Typography>
            )}
        </Grid>
        <Tooltip title={userName}>
          <Avatar
            sx={{
              width: '30px',
              height: '30px',
              bgcolor: stringToColor(userName),
              marginLeft: 3,
            }}
          >
            {stringAvatar(userName)}
          </Avatar>
        </Tooltip>
      </Box>
    );
  },
);

export default ProcessItemInfo;
